import { type ColumnDef, flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useCombineRefs } from '@/hooks/useCombineRefs';
import useUpdateEffect from '@/hooks/useUpdateEffect';
import Checkbox from '../Form/Checkbox';
import TableFooterNav from './TableFooterNav';

interface TableProps {
  cols: ColumnDef<any>[];
  tableData: any;
  onRowClick?: (row: any) => void;
  fetching?: boolean;
  pagination?: {
    page: number;
    pageSize: number;
    pageCount: number;
    currentPage: number;
    setPageSize: (v: number) => void;
    nextPage: () => void;
    previousPage: () => void;
  };
  updateTableData?: (index: number, id: string, value: string | any) => void;
  headerBorderAccent?: boolean;
  defaultColumn?: Partial<ColumnDef<any>>;
  height?: string;
  enableSelection?: boolean;
  onSelectedRowChange?: (selectedRowIds: Record<string, boolean>) => void;
  removedId?: string;
}

interface IndeterminateCheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  indeterminate?: boolean;
}

const IndeterminateCheckbox = forwardRef<HTMLInputElement, IndeterminateCheckboxProps>(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef<HTMLInputElement>(null);
  const combinedRef = useCombineRefs(ref, defaultRef);

  useEffect(() => {
    if (combinedRef?.current) {
      combinedRef.current.indeterminate = indeterminate ?? false;
    }
  }, [combinedRef, indeterminate]);

  return <Checkbox ref={combinedRef} {...rest} />;
});

const Table = forwardRef(
  (
    {
      cols,
      tableData,
      onRowClick,
      pagination,
      fetching,
      headerBorderAccent,
      updateTableData,
      defaultColumn,
      height = 'h-full',
      enableSelection,
      onSelectedRowChange,
      removedId,
    }: TableProps,
    ref
  ) => {
    const [rowSelection, setRowSelection] = useState({});
    const [editableRowIndex, setEditableRowIndex] = useState(null);

    const columns = useMemo<ColumnDef<any>[]>(() => {
      if (!enableSelection) return cols;

      return [
        {
          id: 'selection',
          size: 15,
          header: ({ table }) => (
            <div className=''>
              <IndeterminateCheckbox
                checked={table.getIsAllPageRowsSelected()}
                indeterminate={table.getIsSomePageRowsSelected()}
                onChange={table.getToggleAllPageRowsSelectedHandler()}
              />
            </div>
          ),
          cell: ({ row }) => (
            <div className='w-10'>
              <IndeterminateCheckbox
                checked={row.getIsSelected()}
                indeterminate={row.getIsSomeSelected()}
                onChange={row.getToggleSelectedHandler()}
              />
            </div>
          ),
        },
        ...cols,
      ];
    }, [cols, enableSelection]);

    const data = useMemo(() => tableData, [tableData]);

    const table = useReactTable({
      columns,
      data,
      defaultColumn,
      manualPagination: true,
      pageCount: pagination?.pageCount ?? -1,
      state: {
        pagination: {
          pageIndex: pagination?.page ?? 0,
          pageSize: pagination?.pageSize ?? 10,
        },
        rowSelection,
      },
      onRowSelectionChange: setRowSelection,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      enableRowSelection: enableSelection,
      meta: {
        updateTableData,
        editableRowIndex,
        setEditableRowIndex,
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        toggleRowSelected: (rowId: string, selected: boolean) => {
          table.getRow(rowId)?.toggleSelected(selected);
        },
        toggleAllRowsSelected: (selected: boolean) => {
          table.toggleAllPageRowsSelected(selected);
        },
      }),
      [table]
    );

    useUpdateEffect(() => {
      onSelectedRowChange?.(rowSelection);
    }, [rowSelection]);

    return (
      <>
        <div className={`overflow-x-auto w-full ${height}`}>
          <table className='divide-y divide-gray-200 w-full'>
            <thead className={`bg-gray-50 border-primaryColor-normal ${headerBorderAccent && 'border-l-4'}`}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        width: header.getSize() !== 150 ? header.getSize() : undefined,
                      }}
                      className='px-6 py-4 text-left text-xs font-bold text-neutralColor-normal capitalize tracking-wider'
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            {fetching && (
              <tbody className='bg-white divide-y divide-gray-200 animate-pulse'>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <tr key={num}>
                    {Array.from(Array(columns.length).keys()).map((num) => (
                      <td className='p-6' key={num}>
                        <span className='h-2 rounded-md bg-neutralColor-light w-32 block'></span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
            {!fetching && (
              <tbody className='bg-white divide-y divide-gray-200'>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className='hover:bg-green-50 cursor-pointer' onClick={() => onRowClick?.(row.original)}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize() !== 150 ? cell.column.getSize() : undefined,
                        }}
                        className='px-6 py-4'
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!pagination && (
          <TableFooterNav
            nextPage={table.nextPage}
            previousPage={table.previousPage}
            currentPage={table.getState().pagination.pageIndex + 1}
            totalPage={table.getPageCount()}
            perPageHandler={table.setPageSize}
          />
        )}
        {pagination && (
          <TableFooterNav
            nextPage={pagination.nextPage}
            previousPage={pagination.previousPage}
            currentPage={pagination.currentPage}
            totalPage={table.getPageCount() || 1}
            perPage={pagination.pageSize}
            perPageHandler={pagination.setPageSize}
          />
        )}
      </>
    );
  }
);

export default Table;
